import { SnitcherBody } from './Snitcher';
import { GTMBody } from './GTM';
import { FacebookPixelsBody } from './FacebookTracker';
import { LinkedInTrackerBody } from './LinkedInTracker';
import { PinterestTrackerBody } from './PinterestTracker';
import type { ClientConfig } from '@archipro-website/config';
import { useRootData } from '@modules/root';
import {
    hasSaleOrSharingOptOut,
    shouldAllowThirdPartySharing,
} from './allow-third-party-sharing';
import { isConfiguredPixelId } from './is-configured-pixel-id';

/**
 * Body pixels, all suppressed by the shared third-party-sharing gate (the HTTP
 * `Sec-GPC` header where the market honours GPC, or an explicit sale/sharing
 * opt-out from either the signed-in member's preference or the opt-out cookie).
 * The Facebook, Pinterest, and LinkedIn entries are the server-rendered
 * `<noscript><img>` halves of those trackers; their `<script>` halves live in
 * `ClientAdvertisingPixels` and re-read the browser-visible signals.
 *
 * Snitcher renders nothing at all: it injects its loader after hydration, so it
 * is inert without JavaScript and has no no-JS half to suppress. The GTM
 * noscript iframe does render, and is suppressed outright rather than given the
 * consent-denied treatment `GTMHead` uses — without JavaScript Google Consent
 * Mode cannot apply, so that treatment is not available here.
 *
 * GTM noscript is gated by `isConfiguredPixelId` so placeholder or empty IDs
 * from client config do not emit a broken iframe. Production configs carry real
 * GTM IDs; team/staging configs should too if no-JS GTM fallback is needed.
 */
export const BodyPixels = ({
    gtmId,
    snitcherTrackingId,
    facebookPixelArchiproId,
    facebookPixelClientId,
    pinterestTagArchiproId,
    pinterestTagClientId,
    linkedinPartnerId,
}: Pick<
    ClientConfig['pixels'],
    | 'gtmId'
    | 'snitcherTrackingId'
    | 'facebookPixelArchiproId'
    | 'facebookPixelClientId'
    | 'pinterestTagArchiproId'
    | 'pinterestTagClientId'
    | 'linkedinPartnerId'
>) => {
    const { localisation, globalPrivacyControl, saleSharingOptOut, user } =
        useRootData();
    const allowSharing = shouldAllowThirdPartySharing(
        localisation.countryConfig.honourGlobalPrivacyControl,
        globalPrivacyControl,
        hasSaleOrSharingOptOut(user) || saleSharingOptOut,
    );

    const hasFacebookPixel =
        isConfiguredPixelId(facebookPixelArchiproId) ||
        isConfiguredPixelId(facebookPixelClientId);
    const hasPinterestPixel =
        isConfiguredPixelId(pinterestTagArchiproId) ||
        isConfiguredPixelId(pinterestTagClientId);

    return (
        <>
            {allowSharing && snitcherTrackingId && (
                <SnitcherBody id={snitcherTrackingId} />
            )}
            {allowSharing && isConfiguredPixelId(gtmId) && (
                <GTMBody id={gtmId} />
            )}
            {allowSharing && hasFacebookPixel && (
                <FacebookPixelsBody
                    pixel={facebookPixelArchiproId}
                    pixelClient={facebookPixelClientId}
                />
            )}
            {allowSharing && hasPinterestPixel && (
                <PinterestTrackerBody
                    pixelArchipro={pinterestTagArchiproId}
                    pixelClient={pinterestTagClientId}
                />
            )}
            {allowSharing && isConfiguredPixelId(linkedinPartnerId) && (
                <LinkedInTrackerBody partnerId={linkedinPartnerId} />
            )}
        </>
    );
};
